// Create / edit dialog for an athlete's sport profile (see
// `features/teams-sports.md`). The sport is chosen at creation from the
// sports the athlete does not hold yet and fixed afterwards (one profile per
// athlete per sport). The attribute form is driven entirely by the sport's
// config: each `enum` attribute renders as a single select, each `multiEnum`
// as a multi-value picker — a new sport or attribute added to the catalog
// needs no UI change. Editing also offers deletion, which the server refuses
// while team memberships still link to the profile.

import { useEffect, useMemo, useState } from 'react';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import MenuItem from '@mui/material/MenuItem';
import type { Athlete, Sport, SportProfile } from '../domain';
import { validateSportAttributes } from '../domain';
import {
  createSportProfile,
  deleteSportProfile,
  updateSportProfile,
} from '../data/apiClient';
import { useTranslation } from '../state/I18nContext';

interface SportProfileDialogProps {
  open: boolean;
  /** The athlete the profile belongs to. */
  athlete: Athlete;
  /** The profile being edited, or null to create a new one. */
  profile: SportProfile | null;
  /** The live sport catalog (labels, attribute specs, create-time choice). */
  sports: Sport[];
  /** Sports the athlete already holds a live profile for (excluded on create). */
  existingSportIds: string[];
  onClose: () => void;
  onSaved: (profiles: SportProfile[]) => void;
}

export function SportProfileDialog({
  open,
  athlete,
  profile,
  sports,
  existingSportIds,
  onClose,
  onSaved,
}: SportProfileDialogProps) {
  const { t } = useTranslation();

  const [sportId, setSportId] = useState('');
  // Attribute drafts: enum keys hold a string ('' = not set), multiEnum keys
  // hold a string array. Keys absent from the draft are simply unset.
  const [draft, setDraft] = useState<Record<string, string | string[]>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  // (Re)seed the form whenever the dialog opens for a different target.
  useEffect(() => {
    if (!open) return;
    setSportId(profile?.sportId ?? '');
    const seeded: Record<string, string | string[]> = {};
    for (const [key, value] of Object.entries(profile?.attributes ?? {})) {
      seeded[key] = Array.isArray(value) ? value.map(String) : String(value);
    }
    setDraft(seeded);
    setError(null);
    setConfirmingDelete(false);
  }, [open, profile]);

  const sport = sports.find((s) => s.id === sportId);

  // On create, only sports the athlete does not hold yet are offered.
  const choosable = useMemo(
    () => sports.filter((s) => !existingSportIds.includes(s.id)),
    [sports, existingSportIds],
  );

  const sportLabel = (target: Sport) => {
    const translated = t(`sports.${target.id}`);
    return translated === `sports.${target.id}` ? target.name : translated;
  };
  const attributeLabel = (key: string) => {
    const translated = t(`sports.attributes.${key}`);
    return translated === `sports.attributes.${key}` ? key : translated;
  };

  // The attributes object as it would be submitted: unset entries dropped.
  const attributes = useMemo(() => {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(draft)) {
      if (Array.isArray(value) ? value.length > 0 : value !== '') {
        result[key] = value;
      }
    }
    return result;
  }, [draft]);

  // The controls only produce declared values, so this holds unless the
  // catalog changed under an open dialog; the server re-validates regardless.
  const valid =
    sport !== undefined && validateSportAttributes(sport.config, attributes).valid;

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true);
    setError(null);
    try {
      const profiles = profile
        ? await updateSportProfile(profile.id, { attributes })
        : await createSportProfile(athlete.personalNumber, { sportId, attributes });
      onSaved(profiles);
      onClose();
    } catch (e: unknown) {
      setError(
        e instanceof Error ? e.message : t('admin.athletes.profiles.errors.save'),
      );
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!profile || busy) return;
    setConfirmingDelete(false);
    setBusy(true);
    setError(null);
    try {
      const profiles = await deleteSportProfile(profile.id);
      onSaved(profiles);
      onClose();
    } catch (e: unknown) {
      setError(
        e instanceof Error ? e.message : t('admin.athletes.profiles.errors.delete'),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>
        {profile && sport
          ? t('admin.athletes.profiles.dialog.editTitle', {
              sport: sportLabel(sport),
              name: athlete.fullName,
            })
          : t('admin.athletes.profiles.dialog.createTitle', {
              name: athlete.fullName,
            })}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          {profile ? null : choosable.length === 0 ? (
            <Alert severity="info">
              {t('admin.athletes.profiles.dialog.allAssigned')}
            </Alert>
          ) : (
            <TextField
              select
              label={t('admin.athletes.profiles.dialog.sport')}
              value={sportId}
              onChange={(e) => {
                setSportId(e.target.value);
                // Attribute keys are sport-specific: switching sports resets
                // the draft so no stale keys leak across.
                setDraft({});
              }}
            >
              {choosable.map((choice) => (
                <MenuItem key={choice.id} value={choice.id}>
                  {sportLabel(choice)}
                </MenuItem>
              ))}
            </TextField>
          )}

          {sport &&
            Object.entries(sport.config.attributes).map(([key, spec]) =>
              spec.kind === 'enum' ? (
                <TextField
                  key={key}
                  select
                  label={attributeLabel(key)}
                  value={typeof draft[key] === 'string' ? draft[key] : ''}
                  onChange={(e) =>
                    setDraft((current) => ({ ...current, [key]: e.target.value }))
                  }
                >
                  <MenuItem value="">
                    <em>{t('admin.athletes.profiles.dialog.notSet')}</em>
                  </MenuItem>
                  {spec.values.map((value) => (
                    <MenuItem key={value} value={value}>
                      {value}
                    </MenuItem>
                  ))}
                </TextField>
              ) : (
                <Autocomplete
                  key={key}
                  multiple
                  disableCloseOnSelect
                  options={spec.values}
                  value={Array.isArray(draft[key]) ? (draft[key] as string[]) : []}
                  onChange={(_, value) =>
                    setDraft((current) => ({ ...current, [key]: value }))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label={attributeLabel(key)} />
                  )}
                />
              ),
            )}
        </Stack>
      </DialogContent>
      <DialogActions>
        {profile && (
          <Button
            color="error"
            disabled={busy}
            onClick={() => setConfirmingDelete(true)}
          >
            {t('admin.common.delete')}
          </Button>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Button disabled={busy} onClick={onClose}>
          {t('admin.common.cancel')}
        </Button>
        <Button
          variant="contained"
          disabled={busy || !valid}
          onClick={() => void submit()}
        >
          {t('admin.common.save')}
        </Button>
      </DialogActions>

      <Dialog open={confirmingDelete} onClose={() => setConfirmingDelete(false)}>
        <DialogTitle>{t('admin.athletes.profiles.deleteDialog.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {sport
              ? t('admin.athletes.profiles.deleteDialog.body', {
                  sport: sportLabel(sport),
                  name: athlete.fullName,
                })
              : ''}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmingDelete(false)}>
            {t('admin.common.cancel')}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => void confirmDelete()}
          >
            {t('admin.common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
